import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { _hop } from '../../../Util';
import Btn from '../../../Components/Btn';
import Link from '../../../Components/Link';
import NumericUpDown from '../../../Components/NumericUpDown';
import { Panel, PanelHeader, PanelBody, PanelFooter} from '../../../Components/Panel/Panel';
import { Article } from '../../../Components/Article/Article';
import SectionMessage from '../../../Components/SectionMessage';
import SelectDropdown from '../../../Components/SelectDropdown';
import useEventBroker from '../../../Hooks/useEventBroker';
import useFeature from '../../../../flags/useFeature';
import renderJsxTag from '../../../Translation/translation-utils';
import { useWysiwygEditorContext } from '../../WysiwygEditor/context/WysiwygEditorContext';
import AutocompleteData from '../../../Components/AutocompleteData';
import PanelRecommendations from './PanelRecommendations';

const isClassicShelving = (rule) => rule.ruleConfiguration.contextType === 'ClassicShelving';

export default function PanelRecommendationsAccordingToRuleConsumerConfiguration({ mode = 'campaign' }) {
	const {
		ruleConsumerConfiguration
	} = useWysiwygEditorContext();

	if (ruleConsumerConfiguration === undefined)
		return null;

	if (ruleConsumerConfiguration.hasExplicitConsumers) {
		return <PanelRecommendationsWithParam mode={mode} />;
	} else {
		return <PanelRecommendations mode={mode} />;
	}
}

function PanelRecommendationsWithParam({ mode = 'campaign' }) {

	const {
		listOfShelvingRules,
		isAdmin,
		accountId,
		setupAccountServices
	} = useWysiwygEditorContext();

	const [t] = useTranslation('common');

	const emptySelectItems = [{
		id: 0,
		label: t('recos.choose'),
		shelving: '',
		NumberOfProducts: 4,
		ContextParams: [],
		ruleConfiguration: {}
	}];

	const { subscribe } = useEventBroker();
	const { _ } = useFeature();

	const [panelRecommendationsContext, setPanelRecommendationsContext] = useState();
	const [selectedRules, setSelectedRules] = useState(emptySelectItems);
	const [errorsInSelection, seterrorsInSelection] = useState([]);

	subscribe('evt/campaign/setShelvingRulesRequested', (e) => {
		setPanelRecommendationsContext({ callback: e.detail.callback, value: e.detail.value, from: e.detail?.from ?? 'campaign' });
	});

	useEffect(() => {
		const isValidArrayOfRecommendation = selectedRules.reduce((acc, curr) => {
			let err = { id: curr.id };

			if ((_hop(curr.ruleConfiguration, 'context') && curr.ruleConfiguration.context !== 'Global') && (!_hop(curr, 'ruleScope') && !isClassicShelving(curr))) {
				err.error = t('recos.chooseCtx');
			}

			return [...acc, err];
		}, []).filter(x => x.error);

		const isValid = isValidArrayOfRecommendation.length <= 0;
		if (isValid) {
			seterrorsInSelection([]);
		} else {
			seterrorsInSelection(isValidArrayOfRecommendation);
		}
	}, [selectedRules]);

	const getScopeOptions = (rule) => {
		switch (rule.context) {
			case 'Global':
				return [];
			case 'Category':
				return [
					['categoryCurrentPage', t('recos.catFromCurrentPage')],
					['categoryFromHistory', t('recos.catFromCurrentHistory')],
					['categoryFromCart', t('recos.catFromCart')]];
			case 'Tag':
				return [
					['tagCurrentPage', t('recos.tagFromPage')],
					['tagFromHistory', t('recos.tagFromHistory')],
					['tagFromCart', t('recos.tagFromCart')]];
			case 'ProductGlobal':
			case 'ProductCategory':
			case 'ProductTag':
				return [
					['productCurrentPage', t('recos.productFromPage')],
					['productFromHistory', t('recos.productFromHistory')],
					['productFromCart', t('recos.productFromCart')]];
			default:
				return [];
		}
	};

	const getScopeLabel = (rule) => {
		switch (rule.context) {
			case 'Category':
				return 'Based on';
			case 'Tag':
				return 'Based on';
			case 'ProductGlobal':
			case 'ProductCategory':
			case 'ProductTag':
				return 'Based on';
			default:
				return '';
		}
	};

	const changeRule = (shelvingRuleId, itemId) => {
		const rule = listOfShelvingRules.find(x => x.id === shelvingRuleId);
		const nbOfProducts = selectedRules.length > 0 ? selectedRules[0].NumberOfProducts : 4;
		const ctxParams = selectedRules.length > 0 && selectedRules[0].ruleConfiguration.context == rule.context ? selectedRules[0].ContextParams : [];
		const availableScopes = getScopeOptions(rule);
		const scope = availableScopes.length >= 1 ? availableScopes[0][0] : null;

		setSelectedRules([{
			id: itemId,
			label: rule.name,
			shelving: shelvingRuleId,
			ruleConfiguration: rule,
			NumberOfProducts: nbOfProducts,
			ContextParams: ctxParams,
			ruleScope: scope
		}]);
	};

	const updateRule = (ruleId, update) => {
		const itemToChange = update(selectedRules.find(x => x.id === ruleId));
		let newArray = selectedRules.map(x => x.id === ruleId ? itemToChange : x);
		setSelectedRules(newArray);
	};

	const upNumberOfProductTarget = (e, ruleId) => {
		updateRule(ruleId, rule => ({ ...rule, NumberOfProducts: parseInt(e.target.value) }));
	};

	const changeValueOfRuleScope = (value, ruleId) => {
		updateRule(ruleId, rule => ({ ...rule, ruleScope: value }));
	};

	const changeValueOfContextParams = (value, ruleId) => {
		updateRule(ruleId, rule => ({ ...rule, ContextParams: value }));
	};

	const deleteItemFromArrayOfAlgo = (e) => {
		const tabs = [...selectedRules];
		const newTabs = tabs.filter(x => x.id !== e);
		if (newTabs.length < 1) {
			newTabs.push(emptySelectItems[0]);
		}
		setSelectedRules([...newTabs]);
	};

	const getCategoryParamSelector = (algo) => {
		const handleChangeCategoryContextParam = (value) => changeValueOfContextParams(value, algo.id);

		const value = algo.ContextParams ?? [];

		return <AutocompleteData
			selectedTenant={algo.tenant}
			setupAccountServices={setupAccountServices}
			currentAccount={accountId}
			referenceDataType={'productCategoryType'}
			values={value}
			isMulti={true}
			onChange={(value) => handleChangeCategoryContextParam(value)}
			label='Category types'
			autoCompletePlaceholder='Search for a category type'
		/>;
	};

	const getTagParamSelector = (algo) => {
		const handleChangeTagContextParam = (value) => changeValueOfContextParams(value, algo.id);

		const value = algo.ContextParams ?? [];

		return <AutocompleteData
			selectedTenant={algo.tenant}
			setupAccountServices={setupAccountServices}
			currentAccount={accountId}
			referenceDataType={'productTagKey'}
			values={value}
			isMulti={true}
			onChange={(value) => handleChangeTagContextParam(value)}
			label='Tags'
			autoCompletePlaceholder='Search for a tag'
		/>;
	};

	const displayContextSelector = (algo) => {
		if (mode === 'email_template') return;

		const options = getScopeOptions(algo.ruleConfiguration);
		if (options.length === 0) {
			return <></>;
		}

		const label = getScopeLabel(algo.ruleConfiguration);

		const getContextParamSelector = () => {
			switch (algo.ruleConfiguration.context) {
				case 'Category':
				case 'ProductCategory':
					return getCategoryParamSelector(algo);
				case 'Tag':
				case 'ProductTag':
					return getTagParamSelector(algo);
				case 'ProductGlobal':
				case 'Global':
				default:
					return null;
			}
		};

		const contextParamSelector = getContextParamSelector();

		return (
			<>
				<SelectDropdown
					label={label}
					placeHolder={t('recos.chooseCtx')}
					optionsList={options.map(x => { return { value: x[0], label: x[1] }; })}
					value={algo.ruleScope}
					onChange={(v) => changeValueOfRuleScope(v, algo.id)}
					fullWidth={true}
				/>
				{contextParamSelector}
			</>
		);
	};

	const addSlidePropertyToRuleInfo = (rule, slideProperty) => {
		const addRuleScope = (p) => {
			if (!isClassicShelving(rule)) {
				return { ...p, ruleScope: slideProperty.RuleScope };
			}
			return p;
		};
		return addRuleScope(rule);
	};

	useEffect(() => {
		if (panelRecommendationsContext) {
			const rules = panelRecommendationsContext.value;
			var initialRules = [];
			for (let i = 0; i < rules.length; i++) {
				const slideRule = rules[i];
				const ruleConfiguration = listOfShelvingRules.find(x => x.id === slideRule.Id) || {};
				const label = ruleConfiguration.name || slideRule.Id;

				const ruleWithInfo = addSlidePropertyToRuleInfo({
					id: i + 1,
					label: label,
					shelving: slideRule.Id,
					NumberOfProducts: slideRule.NumberOfProducts,
					ContextParams: slideRule.ContextParams ?? [],
					ruleConfiguration
				}, slideRule);
				initialRules.push(ruleWithInfo);
			}
			setSelectedRules(initialRules.length > 0 ? initialRules : emptySelectItems);
		}
	}, [panelRecommendationsContext]);


	const handleSubmitRecommendations = () => {
		const isValid = errorsInSelection.length <= 0;
		if (isValid) {
			synchronizeSlideShelvingRules();
		}
	};


	const synchronizeSlideShelvingRules = () => {
		const buildScope = (rule, context) => {
			if (isClassicShelving(rule)) {
				return context;
			}
			if (rule.ruleConfiguration.context === 'Global') {
				return 'global';
			}
			return rule.ruleScope;
		};

		const getRuleType = (rule) => {
			if (rule.contextType === 'CompositionWithExplicitConsumer') {
				return 'Composition';
			}
			return 'ProductRecommendation';
		};

		const rulesProperty = [];
		const newSelectedRules = selectedRules.filter(x => x.shelving);
		newSelectedRules && newSelectedRules.forEach(x => {
			const rule = listOfShelvingRules.find(r => r.id === x.shelving);
			if (!rule) return;
			const context = rule.context;
			const scope = buildScope(x, context);
			if (scope && x.NumberOfProducts > 0) {
				let slideProperty = { Id: x.shelving, RuleScope: scope, NumberOfProducts: x.NumberOfProducts, RuleType: getRuleType(x.ruleConfiguration) };
				if (x.ContextParams.length > 0) {
					slideProperty.ContextParams = x.ContextParams;
				}
				rulesProperty.push(slideProperty);
			}
		});
		if (panelRecommendationsContext.from === 'custom') {
			panelRecommendationsContext.callback(rulesProperty, 'ShelvingRules');
		} else {
			panelRecommendationsContext.callback(rulesProperty);
		}

		setPanelRecommendationsContext(false);
	};

	let linkToRecos = '';
	if (isAdmin && accountId) {
		linkToRecos = `/ProductSettings/Reco?ka=${accountId}`;
	} else {
		linkToRecos = '/ProductSettings/Reco';
	}
	return (
		<Panel
			isOpen={!!panelRecommendationsContext}
			width={540}
		>
			<PanelHeader
				onClose={() => setPanelRecommendationsContext(false)}
				title={t('recos.title')}
				primaryAction={
					<Btn
						onClickFunction={() => handleSubmitRecommendations()}
						message="OK"
					/>
				}
			/>
			<PanelBody color='grey'>
				<SectionMessage
					type="information"
					appearance='background_outline'
					marginSize='l'
					links={
						<Link
							message="E-merchandising"
							icon="fas fa-external-link-square-alt fa-sm"
							iconPosition='after'
							href={linkToRecos}
							target="_blank"
						/>
					}>
					{renderJsxTag(t('recos.canCreate'))}
				</SectionMessage>
				<Article innerSize='l'>
					{selectedRules && selectedRules.map((algo, i) =>
						<Fragment key={i}>
							{errorsInSelection.find(el => el.id === algo.id)?.error}

							<SelectDropdown
								label='Select a recommendation rule'
								placeHolder='Choose a rule'
								optionsList={listOfShelvingRules.map(x => { return { 'value': x.id, 'label': x.name }; })}
								value={algo.shelving}
								onChange={(v) => changeRule(v, algo.id)}
								fullWidth={true}
							/>

							{algo.shelving &&
								<>
									{mode !== 'email_template' &&
										displayContextSelector(algo)
									}

									{algo.ruleConfiguration.contextType !== 'CompositionWithExplicitConsumer' &&
										<NumericUpDown
											label={'Number of products'}
											min={1}
											value={algo.NumberOfProducts}
											onChange={(e) => upNumberOfProductTarget(e, algo.id)}
											canEdit={true}
										/>
									}
								</>
							}
						</Fragment>
					)}
				</Article>
			</PanelBody>
			{selectedRules && selectedRules[0] && selectedRules[0].shelving &&
				<PanelFooter
					align='right'
					size='s'
					primaryAction={
						<Btn
							message='Reset'
							icon='fas fa-ban'
							style="ghost"
							color='secondary'
							size="s"
							horizontalSize="xxs"
							onClick={() => deleteItemFromArrayOfAlgo(selectedRules[0].id)}
						/>
					}
				/>
			}
		</Panel>
	);
}
